import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Box,
  Chip,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Download,
  FileDownload,
  TableChart,
  Assessment,
  FilterList,
  Preview
} from '@mui/icons-material';
import { api } from '../../services/api';

const UserExportPanel = () => {
  const [filters, setFilters] = useState({
    role: '',
    agency_id: '',
    is_active: '',
    search: ''
  });
  
  const [filterOptions, setFilterOptions] = useState({
    roles: [],
    agencies: [],
    status_options: [],
    export_formats: []
  });
  
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Load initial data
  useEffect(() => {
    loadFilterOptions();
    loadSummary();
  }, []);

  // Load summary when filters change
  useEffect(() => {
    if (Object.values(filters).some(value => value !== '')) {
      loadSummary();
    }
  }, [filters]);

  const loadFilterOptions = async () => {
    try {
      const response = await api.get('/user-export/available_filters/');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Failed to load filter options:', error);
      setError('Failed to load filter options');
    }
  };

  const loadSummary = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      
      const response = await api.get(`/user-export/export_summary/?${queryParams.toString()}`);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to load summary:', error);
      setError('Failed to load summary data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      role: '',
      agency_id: '',
      is_active: '',
      search: ''
    });
  };

  const exportUsers = async (format) => {
    setExporting(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      
      const endpoint = format === 'excel' ? 'export_users_excel' : 'export_users_csv';
      const url = `/user-export/${endpoint}/?${queryParams.toString()}`;
      
      // Create temporary link to download file
      // Use the same API configuration as other services
      const BACKEND_API_URL = window.AMS_CONFIG?.API_URL || 'http://localhost:8000';
      const API_PATH = window.AMS_CONFIG?.API_PATH || '/api';
      const fullUrl = `${BACKEND_API_URL}${API_PATH}${url}`;
      
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `users_export_${Date.now()}.${format === 'excel' ? 'xlsx' : 'csv'}`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
    } catch (error) {
      console.error('Export failed:', error);
      setError('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const exportUserPermissions = async (userId, format) => {
    try {
      const url = `/user-export/export_user_permissions/?user_id=${userId}&format=${format}`;
      
      // Use the same API configuration as other services
      const BACKEND_API_URL = window.AMS_CONFIG?.API_URL || 'http://localhost:8000';
      const API_PATH = window.AMS_CONFIG?.API_PATH || '/api';
      const fullUrl = `${BACKEND_API_URL}${API_PATH}${url}`;
      
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `user_permissions_${userId}_${Date.now()}.${format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
    } catch (error) {
      console.error('Permission export failed:', error);
      setError('Permission export failed. Please try again.');
    }
  };

  const renderSummaryStats = () => {
    if (!summary) return null;

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="primary">
              {summary.total_users}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Users
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">
              {summary.active_users}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Active Users
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main">
              {summary.admin_users}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Admin Users
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="info.main">
              {summary.total_permissions}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Permissions
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const renderRolesBreakdown = () => {
    if (!summary?.roles_breakdown) return null;

    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Roles Breakdown
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {Object.entries(summary.roles_breakdown).map(([role, count]) => (
            <Chip
              key={role}
              label={`${role}: ${count}`}
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
      </Paper>
    );
  };

  const renderPreviewDialog = () => {
    return (
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Export Preview (First 5 Users)
        </DialogTitle>
        <DialogContent>
          {summary?.preview_data && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Full Name</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Agency</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Permissions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.preview_data.map((user, index) => (
                    <TableRow key={index}>
                      <TableCell>{user.Username}</TableCell>
                      <TableCell>{user.Email}</TableCell>
                      <TableCell>{user['Full Name']}</TableCell>
                      <TableCell>{user.Role}</TableCell>
                      <TableCell>{user.Agency}</TableCell>
                      <TableCell>
                        <Chip
                          label={user['Is Active'] ? 'Active' : 'Inactive'}
                          color={user['Is Active'] ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            onClick={() => exportUserPermissions(user['User ID'], 'excel')}
                          >
                            Excel
                          </Button>
                          <Button
                            size="small"
                            onClick={() => exportUserPermissions(user['User ID'], 'csv')}
                          >
                            CSV
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assessment />
          User Management Export
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Filters Section */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList />
            Filters
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={filters.role}
                  label="Role"
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                >
                  <MenuItem value="">All Roles</MenuItem>
                  {filterOptions.roles.map((role) => (
                    <MenuItem key={typeof role === 'object' ? role.value || role.name : role} 
                              value={typeof role === 'object' ? role.value || role.name : role}>
                      {typeof role === 'object' ? role.label || role.name : role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Agency</InputLabel>
                <Select
                  value={filters.agency_id}
                  label="Agency"
                  onChange={(e) => handleFilterChange('agency_id', e.target.value)}
                >
                  <MenuItem value="">All Agencies</MenuItem>
                  {filterOptions.agencies.map((agency) => (
                    <MenuItem key={agency.agency_id || agency.id} value={agency.agency_id || agency.id}>
                      {agency.agency_name || agency.name || 'Unknown Agency'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.is_active}
                  label="Status"
                  onChange={(e) => handleFilterChange('is_active', e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  {filterOptions.status_options.map((option) => (
                    <MenuItem key={option.value || option.id} value={option.value || option.id}>
                      {option.label || option.name || 'Unknown Status'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                placeholder="Username, email, name..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={clearFilters}
              size="small"
            >
              Clear Filters
            </Button>
            <Button
              variant="outlined"
              onClick={() => setPreviewOpen(true)}
              startIcon={<Preview />}
              size="small"
              disabled={!summary}
            >
              Preview Data
            </Button>
          </Box>
        </Paper>

        {/* Summary Section */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {renderSummaryStats()}
            {renderRolesBreakdown()}
          </>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Export Actions */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          Export Options
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <TableChart sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6">Excel Export</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Complete user data with formatting and multiple sheets
              </Typography>
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={() => exportUsers('excel')}
                disabled={exporting || !summary}
                fullWidth
              >
                {exporting ? 'Exporting...' : 'Download Excel'}
              </Button>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Download sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h6">CSV Export</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Raw data format for further processing and analysis
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<FileDownload />}
                onClick={() => exportUsers('csv')}
                disabled={exporting || !summary}
                fullWidth
              >
                {exporting ? 'Exporting...' : 'Download CSV'}
              </Button>
            </Paper>
          </Grid>
        </Grid>

        {/* Additional Info */}
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Export includes:</strong> User details, roles, permissions, capabilities, 
            timestamps, and agency information. Individual user permissions can be exported 
            from the preview table.
          </Typography>
        </Alert>

        {renderPreviewDialog()}
      </CardContent>
    </Card>
  );
};

export default UserExportPanel;